<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { organizationsApi, type Organization } from '@/services/api/organizations';
import { useAuthStore } from '@/stores/auth';
import { landingRouteFor } from '@/router/landing';
import { useNotifier } from '@/composables/notifier';

const auth = useAuthStore();
const router = useRouter();
const notify = useNotifier();

const organizations = ref<Organization[]>([]);
const selected = ref<number | null>(null);

async function reload(): Promise<void> {
  organizations.value = await organizationsApi.index();
}

async function switchTo(): Promise<void> {
  if (selected.value === null) return;
  // Optimistically update the local auth store; the next page-load reads
  // the new organization_id from the user record.
  const currentUser = auth.currentUser;
  if (currentUser) {
    auth.setSession({ ...currentUser, organization_id: selected.value }, auth.token);
  }
  notify.info('Organization switched.');
  // Legacy reloaded to the user's landing page (orders) after switching; route
  // there rather than `dashboard`, which no role's nav table grants access to.
  await router.replace(landingRouteFor(auth));
}

onMounted(reload);
</script>

<template>
  <section class="resource">
    <h1>Switch Organization</h1>
    <form class="resource__new" @submit.prevent="switchTo">
      <select v-model.number="selected">
        <option :value="null">Select an organization…</option>
        <option v-for="o in organizations" :key="o.id" :value="o.id">
          {{ o.name }}<span v-if="o.city">— {{ o.city }}, {{ o.state }}</span>
        </option>
      </select>
      <button type="submit" :disabled="selected === null">Switch</button>
    </form>
  </section>
</template>

<style scoped src="@/assets/styles/resource-list.scss"></style>
